// Helpers para derivar el estado "en vivo" de los lobbies.
// Un lobby (viaje) trae Journey.origin/destination con coordenadas, departure_time
// y estimated_time. Con eso calculamos el progreso del trayecto sin backend extra.

export const toTrips = data => (Array.isArray(data) ? data : [])

// Las coordenadas pueden venir como [lat, lng] o [lng, lat] según el origen del
// dato. Detectamos el orden por los rangos de Argentina/Uruguay y devolvemos
// siempre [lng, lat] que es lo que espera d3-geo.
const inLat = v => v >= -60 && v <= -15
const inLng = v => v >= -80 && v <= -45
export const coordLngLat = point => {
  if (!point || !Array.isArray(point.coordinates) || point.coordinates.length < 2) return null
  const [a, b] = point.coordinates
  if (typeof a !== 'number' || typeof b !== 'number') return null
  const looksLatLng = inLat(a) && inLng(b) // [lat, lng]
  const looksLngLat = inLng(a) && inLat(b) // [lng, lat]
  if (looksLngLat && !looksLatLng) return [a, b]
  if (looksLatLng && !looksLngLat) return [b, a]
  // Zona de solapamiento o sin pistas: el backend guarda [lat, lng] -> swap.
  return [b, a]
}

const ms = iso => {
  if (!iso) return NaN
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? NaN : t
}

export const departureMs = trip => ms(trip?.Journey?.departure_time)
export const arrivalMs = trip => ms(trip?.Journey?.estimated_time)

// ¿La salida es el mismo día que `refMs` (por defecto, hoy)?
export const isToday = (trip, refMs) => {
  const t = departureMs(trip)
  if (Number.isNaN(t)) return false
  const d = new Date(t)
  const now = refMs ? new Date(refMs) : new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

const isClosed = trip => Boolean(trip?.cancelled || trip?.completed)

// Progreso 0..1 del trayecto en el instante `now` (ms).
export const progress = (trip, now) => {
  const dep = departureMs(trip)
  const arr = arrivalMs(trip)
  if (Number.isNaN(dep) || Number.isNaN(arr) || arr <= dep) return 0
  return Math.min(1, Math.max(0, (now - dep) / (arr - dep)))
}

// Viaje "en curso": ya salió, no llegó, no cancelado/completado y con coordenadas.
export const isInTransit = (trip, now) => {
  if (isClosed(trip)) return false
  const dep = departureMs(trip)
  const arr = arrivalMs(trip)
  if (Number.isNaN(dep) || Number.isNaN(arr)) return false
  if (now < dep || now > arr) return false
  return Boolean(coordLngLat(trip?.Journey?.origin) && coordLngLat(trip?.Journey?.destination))
}

// Viaje de hoy que todavía no salió (para el pulso en la ciudad de origen).
export const isUpcomingToday = (trip, now) => {
  if (isClosed(trip)) return false
  return isToday(trip) && now < departureMs(trip)
}

export const passengerCount = trip => {
  const companions = Array.isArray(trip?.companions) ? trip.companions : []
  const seats = companions.reduce((acc, c) => acc + (Number(c?.seats) || 0), 0)
  return seats > 0 ? seats : companions.length
}

export const localityName = point =>
  point?.locality || point?.formatted_address?.split(',')[0] || point?.specific_address || '—'

export const currencySymbol = c => (c === 'ARS' ? '$AR' : c === 'UYU' ? '$U' : '$')

export const formatMoney = (price, currency) => {
  if (price == null || Number.isNaN(Number(price))) return null
  const n = Number(price)
  const formatted = n >= 1000 ? n.toLocaleString('es-AR') : String(n)
  return `${currencySymbol(currency)} ${formatted}`
}

// Clave de ruta (origen→destino redondeado) para agrupar viajes de una misma ruta.
export const routeKey = trip => {
  const o = trip?.Journey?.origin?.coordinates
  const d = trip?.Journey?.destination?.coordinates
  if (!Array.isArray(o) || !Array.isArray(d)) return trip?._id
  const r = v => (typeof v === 'number' ? v.toFixed(2) : '?')
  return `${r(o[0])},${r(o[1])}>${r(d[0])},${r(d[1])}`
}

export const formatTime = iso => {
  const t = ms(iso)
  if (Number.isNaN(t)) return '--:--'
  return new Date(t).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
}
